import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { PillButton } from "@tento-ui/components/pill-button";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { EventCategories, EventCategory } from "@tento-lona";

@component({
  name: "lona-calendar-category-selector",
})
export class CalendarCategorySelector extends LonaWebComponent {
  controller: Option<CalendarCategorySelector.ControllerLike>;
  /** The client's event categories (`sdk.events.categories`) the pills show. */
  #categories: Option<EventCategories> = null;

  static makeWith(
    controller: CalendarCategorySelector.ControllerLike,
    categories: EventCategories,
  ): CalendarCategorySelector {
    return CalendarCategorySelector.make().bind(controller, categories);
  }

  bind(controller: CalendarCategorySelector.ControllerLike, categories: EventCategories): this {
    this.controller = controller;
    this.#categories = categories;
    this.rebind();
    return this;
  }

  private cleanupPills(): void {
    for (const $child of this.$("categories").children) {
      GESTURE_MANAGER.unregister($child as HTMLElement);
    }
  }

  rebind(): void {
    this.cleanupPills();
    DomUtils.clearChildren(this.$("categories"));

    const controller = this.controller;
    const categories = this.#categories;
    if (!controller || !categories) return;

    for (const categoryItem of categories.all()) {
      const isSelected =
        controller.allSelected ||
        controller.selectedCategories.has(categoryItem.name);
      const $pill = PillButton.makeWith(
        `${categoryItem.emoji} ${categoryItem.name.charAt(0).toUpperCase() + categoryItem.name.slice(1)}`,
        {
          color: "var(--text-pri)",
          background: isSelected ? categoryItem.colorOpt() ?? "var(--gray)" : "transparent",
          border: `2px solid ${categoryItem.colorOpt() ?? "var(--gray)"}`,
          borderRadius: 8,
          padding: "6px 12px",
          fontSize: "var(--p-size)",
        },
      );
      $pill.style.cursor = "pointer";
      $pill.toggleAttribute("selected", isSelected);

      GESTURE_MANAGER.addPointerEvent($pill, {
        onClick: () => {
          const newSelected = !isSelected;
          controller.onChange?.(categoryItem.name, newSelected);
        },
      });

      this.$("categories").appendChild($pill);
    }
  }

  static $styles = [
    css`
      :host {
        padding: 8px 16px;
      }

      #label-container {
        margin-bottom: 12px;
        color: var(--text-pri);
      }

      #categories {
        flex-wrap: wrap;
        gap: 8px;
      }
    `,
  ];

  static $html = html`
    <std-col id="root">
      <std-col id="label-container">
        <p>Event Categories</p>
      </std-col>
      <std-row id="categories"></std-row>
    </std-col>
  `;
}

export namespace CalendarCategorySelector {
  export type ControllerLike = {
    readonly allSelected: boolean;
    readonly selectedCategories: Set<EventCategory>;
    onChange: (category: EventCategory, selected: boolean) => void;
  };

  export class State {
    constructor(
      public allSelected: boolean,
      public selectedCategories: Set<EventCategory>,
    ) {}

    serialize(): SerializedState {
      return {
        allSelected: this.allSelected,
        selectedCategories: Array.from(this.selectedCategories),
      };
    }

    static deserialize(data: SerializedState): State {
      return new State(data.allSelected, new Set(data.selectedCategories));
    }
  }

  export interface SerializedState {
    allSelected: boolean;
    selectedCategories: EventCategory[];
  }

  export class Controller implements ControllerLike {
    onChange: (category: EventCategory, selected: boolean) => void;

    constructor(
      public readonly state: State,
      private onStateChanged?: (state: State) => void,
    ) {
      this.onChange = (category: EventCategory, selected: boolean) => {
        if (this.state.allSelected) {
          const allCategories = EventCategories.NAMES;
          this.state.selectedCategories.clear();
          for (const cat of allCategories) {
            if (cat !== category) {
              this.state.selectedCategories.add(cat);
            }
          }
          this.state.allSelected = false;
        } else {
          if (selected) {
            this.state.selectedCategories.add(category);
          } else {
            this.state.selectedCategories.delete(category);
          }
        }

        this.onStateChanged?.(this.state);
      };
    }

    get allSelected(): boolean {
      return this.state.allSelected;
    }

    get selectedCategories(): Set<EventCategory> {
      return this.state.selectedCategories;
    }
  }
}
